import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { KiMarkComponent } from '../../../components/ki/ki-mark/ki-mark.component';
import { KiVerbindungService } from '../../../services/ki-verbindung.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { createDirtyTracker } from '../../../shared/forms/dirty-tracker';
import {
  gegenstelleVon,
  KI_ANBIETER,
  KI_AUFBEWAHRUNG_OPTIONEN,
  KI_KONTEXTFENSTER_MAX,
  KI_MODELL_MAX,
  KI_TOKEN_MAX,
  KiAnbieterMeta,
  KiAnbieterTyp,
  KiProjektFunktionen,
  KiRegion,
  KiVerbindung,
  KiVerbindungSpeichern,
  KiVerbindungTestErgebnis,
  KiVerbindungZustand,
  regionVonEndpunkt,
} from '../../../models/ki-verbindung.model';

type KiFunktion = keyof KiProjektFunktionen;

export interface KiVerbindungZustandMeldung {
  dirty: boolean;
  valid: boolean;
}

interface KiAufbewahrungOption {
  tage: number;
  labelKey: string;
}

interface KiEntwurf {
  aktiv: boolean;
  anbieter: KiAnbieterTyp | null;
  modell: string;
  /** Leer bei Anbietern mit festem Endpunkt — der feste Wert wird nie gesendet. */
  endpunkt: string;
  apiToken: string;
  kontextfenster: number | null;
  toolCalling: boolean;
  funktionen: KiProjektFunktionen;
  aufbewahrungTage: number;
}

const BEKANNTE_FEHLER = [
  'KI_ANBIETER_NICHT_FREIGEGEBEN',
  'KI_REGION_NICHT_ERLAUBT',
  'KI_ENDPUNKT_NICHT_ERLAUBT',
  'KI_AZURE_ENDPUNKT_UNGUELTIG',
  'KI_AUFBEWAHRUNG_ZU_LANG',
  'KI_FIRMA_AUS',
  'KI_PROJEKT_GESPERRT',
];

type KiTestStand =
  | { status: 'laeuft' }
  | { status: 'ok'; modell: string; dauerMs: number }
  | { status: 'fehler'; key: string | null; text: string | null };

interface KiTokenMenge {
  wert: string;
  mio: boolean;
}

interface KiVerbrauch {
  verbraucht: KiTokenMenge;
  kontingent: KiTokenMenge | null;
  prozent: number;
}

/** Felder, aus denen das Backend die Gültigkeit der Verbindung ableitet; jede Änderung verlangt einen neuen Test. */
const VERBINDUNGSFELDER: (keyof KiEntwurf)[] = ['anbieter', 'modell', 'endpunkt', 'apiToken'];

const FEHLER_DES_TESTS = ['KI_TOKEN_UNGUELTIG', 'KI_ANBIETER_NICHT_ERREICHBAR', 'VALIDATION_FAILED'];
const TEST_FEHLER_ALLGEMEIN = 'ki.verbindung.test.fehler.allgemein';
const TOKENS_PRO_MIO = 1_000_000;

function testFehlerKey(code: string | null | undefined): string | null {
  if (!code) return null;
  if (BEKANNTE_FEHLER.includes(code)) return `ki.verbindung.fehler.${code}`;
  return FEHLER_DES_TESTS.includes(code) ? `ki.verbindung.test.fehler.${code}` : null;
}

/** Ab einer Million in Mio. mit höchstens einer Nachkommastelle wie im Design („1,2 Mio. von 5 Mio.“). */
function tokenMenge(tokens: number, sprache: string): KiTokenMenge {
  if (tokens < TOKENS_PRO_MIO) return { wert: new Intl.NumberFormat(sprache).format(tokens), mio: false };
  const wert = new Intl.NumberFormat(sprache, { maximumFractionDigits: 1 }).format(tokens / TOKENS_PRO_MIO);
  return { wert, mio: true };
}

const LEERER_ENTWURF: KiEntwurf = {
  aktiv: false,
  anbieter: null,
  modell: '',
  endpunkt: '',
  apiToken: '',
  kontextfenster: null,
  toolCalling: false,
  funktionen: { testdesign: false, pipelineAnalyse: false },
  aufbewahrungTage: 0,
};

function metaVon(anbieter: KiAnbieterTyp | null | undefined): KiAnbieterMeta | null {
  return KI_ANBIETER.find((a) => a.typ === anbieter) ?? null;
}

function effektiverEndpunkt(anbieter: KiAnbieterTyp | null | undefined, endpunkt: string | null | undefined): string {
  return metaVon(anbieter)?.festerEndpunkt ?? endpunkt ?? '';
}

function entwurfAus(v: KiVerbindung): KiEntwurf {
  return {
    aktiv: v.aktiv,
    anbieter: v.anbieter ?? null,
    modell: v.modell ?? '',
    endpunkt: metaVon(v.anbieter)?.festerEndpunkt ? '' : (v.endpunkt ?? ''),
    apiToken: '',
    kontextfenster: v.kontextfenster,
    toolCalling: v.toolCalling,
    funktionen: { ...v.funktionen },
    aufbewahrungTage: v.aufbewahrungTage,
  };
}

/**
 * Karte „KI-Verbindung“ im Projekt-Formular (page-ki-einstellungen.jsx:201-302). Lädt ihren
 * Stand selbst; gespeichert wird über das Projekt-Speichern, das {@link speichern} aufruft.
 */
@Component({
  selector: 'app-ki-verbindung-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, DatePipe, NgTemplateOutlet, MatButton, MatIcon, KiMarkComponent],
  templateUrl: './ki-verbindung-card.component.html',
  styleUrl: './ki-verbindung-card.component.scss',
})
export class KiVerbindungCardComponent implements OnInit {
  private readonly service = inject(KiVerbindungService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly sprache = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.currentLang,
  });
  private testAbo: Subscription | null = null;

  readonly projectId = input.required<string>();
  readonly nurLesen = input(false);
  /** Meldet Änderungsstand und Gültigkeit an das Projekt-Formular (Speichern-Button, Dirty-Hinweis). */
  readonly zustandGemeldet = output<KiVerbindungZustandMeldung>();

  readonly anbieterListe = KI_ANBIETER;
  readonly funktionsListe: KiFunktion[] = ['testdesign', 'pipelineAnalyse'];
  readonly modellMax = KI_MODELL_MAX;
  readonly endpunktMax = FIELD_LENGTHS.URL_MAX;
  readonly tokenMax = KI_TOKEN_MAX;
  readonly kontextfensterMax = KI_KONTEXTFENSTER_MAX;

  readonly ladestatus = signal<'laedt' | 'fehler' | 'bereit'>('laedt');
  private readonly geladen = signal<KiVerbindung | null>(null);
  readonly entwurf = signal<KiEntwurf>(LEERER_ENTWURF);
  readonly erweitertOffen = signal(false);
  readonly tokenErsetzen = signal(false);
  readonly speicherFehlerKey = signal<string | null>(null);
  readonly test = signal<KiTestStand | null>(null);

  private readonly dirtyTracker = createDirtyTracker(this.entwurf, LEERER_ENTWURF);
  readonly isDirty = computed(() => this.ladestatus() === 'bereit' && this.dirtyTracker.isDirty());

  readonly gesperrt = computed(() => {
    const zustand = this.geladen()?.zustand;
    return zustand === 'FIRMA_AUS' || zustand === 'GESPERRT';
  });
  readonly zustand = computed<KiVerbindungZustand>(() => {
    if (this.gesperrt()) return this.geladen()!.zustand;
    return this.entwurf().aktiv ? 'AN' : 'AUS';
  });
  readonly projektAn = computed(() => !this.gesperrt() && this.entwurf().aktiv);

  readonly anbieterMeta = computed(() => metaVon(this.entwurf().anbieter));
  readonly festerEndpunkt = computed(() => this.anbieterMeta()?.festerEndpunkt ?? null);
  readonly endpunktPflicht = computed(() => !!this.anbieterMeta() && !this.festerEndpunkt());
  readonly tokenOptional = computed(() => this.anbieterMeta()?.tokenOptional ?? false);
  /** Solange Anbieter und Endpunkt unverändert sind, zählt die Region des Backends (etwa Bestand bei Azure). */
  readonly region = computed<KiRegion | null>(() => {
    const meta = this.anbieterMeta();
    if (!meta) return null;
    const geladen = this.geladen();
    const entwurf = this.entwurf();
    const unveraendert =
      !!geladen?.region && entwurf.anbieter === geladen.anbieter && entwurf.endpunkt === entwurfAus(geladen).endpunkt;
    if (unveraendert) return geladen.region!;
    return meta.region ?? regionVonEndpunkt(entwurf.endpunkt);
  });

  /**
   * Das gespeicherte Token gilt wie im Backend nur für denselben Anbieter und dieselbe Gegenstelle
   * (Schema, Host, effektiver Port); bei jedem Wechsel verlangt die Karte ein neues, Ollama verliert es.
   */
  readonly tokenGespeichertAm = computed(() => {
    const geladen = this.geladen();
    const entwurf = this.entwurf();
    if (!geladen?.tokenGespeichertAm) return null;
    const gleicherAnbieter = entwurf.anbieter === (geladen.anbieter ?? null);
    const neu = gegenstelleVon(entwurf.anbieter, effektiverEndpunkt(entwurf.anbieter, entwurf.endpunkt));
    const alt = gegenstelleVon(geladen.anbieter, effektiverEndpunkt(geladen.anbieter, geladen.endpunkt));
    return gleicherAnbieter && neu === alt ? geladen.tokenGespeichertAm : null;
  });
  readonly tokenFeldSichtbar = computed(() => !this.tokenGespeichertAm() || this.tokenErsetzen());
  readonly tokenPflicht = computed(() => !!this.anbieterMeta() && !this.tokenOptional() && !this.tokenGespeichertAm());

  readonly maxAufbewahrungTage = computed(() => this.geladen()?.maxAufbewahrungTage ?? 0);

  /**
   * Ein gespeicherter Wert außerhalb der Auswahl (etwa nach gesenktem Maximum) wird als eigene
   * Option gezeigt — sonst zeigt der Browser die erste Option, während der Entwurf den alten Wert hält.
   */
  readonly aufbewahrungOptionen = computed<KiAufbewahrungOption[]>(() => {
    const max = this.maxAufbewahrungTage();
    const regulaer = KI_AUFBEWAHRUNG_OPTIONEN.filter((tage) => tage <= max);
    const gespeichert = this.geladen()?.aufbewahrungTage;
    const alle =
      gespeichert && !regulaer.includes(gespeichert) ? [...regulaer, gespeichert].sort((a, b) => a - b) : regulaer;
    const hoechste = regulaer.at(-1);
    return alle.map((tage) => ({
      tage,
      labelKey:
        tage > max
          ? 'ki.verbindung.aufbewahrungUeberMaximum'
          : tage === hoechste
            ? 'ki.verbindung.aufbewahrungMaximum'
            : 'ki.verbindung.aufbewahrungTage',
    }));
  });

  /** Ohne Änderung und bei ausgeschalteter KI wird nichts gesendet; dann sperrt der Wert das Projekt nicht. */
  readonly aufbewahrungUeberMaximum = computed(
    () => this.entwurf().aufbewahrungTage > this.maxAufbewahrungTage() && (this.projektAn() || this.isDirty()),
  );

  readonly isValid = computed(
    () => !this.aufbewahrungUeberMaximum() && (!this.isDirty() || this.pflichtfelderErfuellt()),
  );

  readonly testLaeuft = computed(() => this.test()?.status === 'laeuft');
  /** Wie im Design (page-ki-einstellungen.jsx:272) ohne Vorprüfung: Lücken meldet das Backend als Testergebnis. */
  readonly testMoeglich = computed(() => !this.nurLesen() && !this.testLaeuft());

  /** Region, die der Mandant ausschließt; null, solange er keine Regionen mitliefert oder sie passt. */
  private readonly verboteneRegion = computed<KiRegion | null>(() => {
    const erlaubt = this.geladen()?.erlaubteRegionen;
    const region = this.region();
    return erlaubt && region && !erlaubt.includes(region) ? region : null;
  });
  /** Der Regionsfehler des Backends nennt die Region nicht; ohne bekannte Ursache ist es das Drittland. */
  readonly regionImFehler = computed<KiRegion>(() => this.verboteneRegion() ?? 'DRITTLAND');

  private readonly verbindungUnveraendert = computed(() => {
    const geladen = this.geladen();
    if (!geladen) return false;
    const gespeichert = entwurfAus(geladen);
    const entwurf = this.entwurf();
    return (
      entwurf.anbieter === gespeichert.anbieter &&
      entwurf.modell.trim() === gespeichert.modell.trim() &&
      entwurf.endpunkt.trim() === gespeichert.endpunkt.trim() &&
      !entwurf.apiToken.trim()
    );
  });

  /** Gültigkeit laut Backend; sie gilt nur, solange die Verbindung so ist, wie sie geprüft wurde. */
  readonly gespeichertGueltig = computed(
    () => !this.test() && !!this.geladen()?.verbindungGueltig && this.verbindungUnveraendert(),
  );
  readonly getestetAm = computed(() => this.geladen()?.verbindungGetestetAm ?? null);
  readonly erneutTesten = computed(
    () =>
      !this.nurLesen() &&
      !!this.anbieterMeta() &&
      !this.testLaeuft() &&
      this.test()?.status !== 'ok' &&
      !this.gespeichertGueltig(),
  );

  /** Wie im Design (page-ki-einstellungen.jsx:275): Freigabe und Region kennt die Karte schon vor dem Test. */
  readonly vorabFehlerKey = computed(() => {
    const anbieter = this.entwurf().anbieter;
    if (this.test() || !anbieter) return null;
    if (!this.istErlaubt(anbieter)) return 'ki.verbindung.fehler.KI_ANBIETER_NICHT_FREIGEGEBEN';
    return this.verboteneRegion() ? 'ki.verbindung.fehler.KI_REGION_NICHT_ERLAUBT' : null;
  });

  readonly verbrauch = computed<KiVerbrauch | null>(() => {
    const geladen = this.geladen();
    if (!geladen) return null;
    const sprache = this.sprache() || 'de';
    const verbraucht = Math.max(geladen.verbrauchTokensMonat ?? 0, 0);
    const kontingent = geladen.kontingentTokensProMonat ?? null;
    if (kontingent === null) return { verbraucht: tokenMenge(verbraucht, sprache), kontingent: null, prozent: 0 };
    return {
      verbraucht: tokenMenge(verbraucht, sprache),
      kontingent: tokenMenge(kontingent, sprache),
      prozent: kontingent > 0 ? Math.min(100, (verbraucht * 100) / kontingent) : 100,
    };
  });

  constructor() {
    effect(() => this.zustandGemeldet.emit({ dirty: this.isDirty(), valid: this.isValid() }));
  }

  ngOnInit(): void {
    this.service
      .get(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (verbindung) => this.uebernehmen(verbindung),
        error: () => this.ladestatus.set('fehler'),
      });
  }

  speichern(): Observable<boolean> {
    if (!this.isDirty()) return of(true);
    return this.service.speichern(this.projectId(), this.anfrage()).pipe(
      map((verbindung) => {
        this.uebernehmen(verbindung);
        return true;
      }),
      catchError((error: HttpErrorResponse) => {
        this.speicherFehlerKey.set(this.fehlerKey(error));
        return of(false);
      }),
    );
  }

  testen(): void {
    if (!this.testMoeglich()) return;
    const modell = this.entwurf().modell.trim();
    this.test.set({ status: 'laeuft' });
    this.testAbo = this.service
      .testen(this.projectId(), this.anfrage())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ergebnis) => this.test.set(this.testStand(ergebnis, modell)),
        error: (error: HttpErrorResponse) => this.test.set(this.testFehler(error)),
      });
  }

  istErlaubt(anbieter: KiAnbieterTyp): boolean {
    return this.geladen()?.erlaubteAnbieter.includes(anbieter) ?? false;
  }

  funktionErlaubt(funktion: KiFunktion): boolean {
    return this.geladen()?.erlaubteFunktionen[funktion] ?? false;
  }

  projektUmschalten(): void {
    if (this.gesperrt() || this.nurLesen()) return;
    this.aendern({ aktiv: !this.entwurf().aktiv });
  }

  anbieterWaehlen(wert: string): void {
    const anbieter = (wert || null) as KiAnbieterTyp | null;
    const geladen = this.geladen();
    const behaeltEndpunkt = !metaVon(anbieter)?.festerEndpunkt && anbieter === (geladen?.anbieter ?? null);
    this.aendern({ anbieter, endpunkt: behaeltEndpunkt ? (geladen?.endpunkt ?? '') : '' });
  }

  modellAendern(modell: string): void {
    this.aendern({ modell });
  }

  endpunktAendern(endpunkt: string): void {
    this.aendern({ endpunkt });
  }

  tokenAendern(apiToken: string): void {
    this.aendern({ apiToken });
  }

  kontextfensterAendern(wert: string): void {
    const zahl = Number(wert);
    this.aendern({ kontextfenster: wert.trim() && Number.isFinite(zahl) ? zahl : null });
  }

  toolCallingUmschalten(): void {
    this.aendern({ toolCalling: !this.entwurf().toolCalling });
  }

  funktionUmschalten(funktion: KiFunktion): void {
    const funktionen = this.entwurf().funktionen;
    this.aendern({ funktionen: { ...funktionen, [funktion]: !funktionen[funktion] } });
  }

  aufbewahrungWaehlen(wert: string): void {
    this.aendern({ aufbewahrungTage: Number(wert) });
  }

  private aendern(aenderung: Partial<KiEntwurf>): void {
    this.entwurf.update((entwurf) => ({ ...entwurf, ...aenderung }));
    this.speicherFehlerKey.set(null);
    if (VERBINDUNGSFELDER.some((feld) => feld in aenderung)) this.testVerwerfen();
  }

  private testVerwerfen(): void {
    this.testAbo?.unsubscribe();
    this.testAbo = null;
    this.test.set(null);
  }

  private testStand(ergebnis: KiVerbindungTestErgebnis, modell: string): KiTestStand {
    if (ergebnis.ok) return { status: 'ok', modell, dauerMs: ergebnis.dauerMs ?? 0 };
    const key = testFehlerKey(ergebnis.fehlercode);
    if (key) return { status: 'fehler', key, text: null };
    // Unbekannte Codes eines neueren Backends zeigen dessen Text statt einer nichtssagenden Meldung.
    const text = ergebnis.fehlertext?.trim();
    return text ? { status: 'fehler', key: null, text } : { status: 'fehler', key: TEST_FEHLER_ALLGEMEIN, text: null };
  }

  private testFehler(error: HttpErrorResponse): KiTestStand {
    const code = (error.error as { code?: string } | null)?.code;
    const key = error.status === 403 ? 'ki.verbindung.test.fehler.keinRecht' : testFehlerKey(code);
    return { status: 'fehler', key: key ?? TEST_FEHLER_ALLGEMEIN, text: null };
  }

  private uebernehmen(verbindung: KiVerbindung): void {
    const entwurf = entwurfAus(verbindung);
    this.testVerwerfen();
    this.geladen.set(verbindung);
    this.entwurf.set(entwurf);
    this.dirtyTracker.reset(entwurf);
    this.tokenErsetzen.set(false);
    this.speicherFehlerKey.set(null);
    this.ladestatus.set('bereit');
  }

  private pflichtfelderErfuellt(): boolean {
    const entwurf = this.entwurf();
    const kontext = entwurf.kontextfenster;
    if (kontext === null || !Number.isInteger(kontext) || kontext < 1 || kontext > KI_KONTEXTFENSTER_MAX) return false;
    if (entwurf.modell.length > KI_MODELL_MAX || entwurf.endpunkt.length > FIELD_LENGTHS.URL_MAX) return false;
    if (entwurf.apiToken.length > KI_TOKEN_MAX) return false;
    if (!this.anbieterMeta()) return true;
    if (!entwurf.modell.trim()) return false;
    if (this.endpunktPflicht() && !entwurf.endpunkt.trim()) return false;
    return !this.tokenPflicht() || !!entwurf.apiToken.trim();
  }

  private anfrage(): KiVerbindungSpeichern {
    const entwurf = this.entwurf();
    const token = entwurf.apiToken.trim();
    return {
      aktiv: entwurf.aktiv,
      anbieter: entwurf.anbieter,
      modell: entwurf.modell.trim() || null,
      endpunkt: this.festerEndpunkt() ? null : entwurf.endpunkt.trim() || null,
      ...(token ? { apiToken: token } : {}),
      kontextfenster: entwurf.kontextfenster ?? 0,
      toolCalling: entwurf.toolCalling,
      funktionen: { ...entwurf.funktionen },
      aufbewahrungTage: entwurf.aufbewahrungTage,
    };
  }

  private fehlerKey(error: HttpErrorResponse): string {
    const code = (error.error as { code?: string } | null)?.code ?? '';
    return `ki.verbindung.fehler.${BEKANNTE_FEHLER.includes(code) ? code : 'allgemein'}`;
  }
}
